/**
 * useCreativeOsHome — React Query hooks for the dashboard home view.
 *
 * LIVE: each hook reads the real backend (React Query) for the brand selected in
 * workspaceStore. Views consume ONLY these hooks, so the cockpit shares the
 * app's caches (e.g. the Brand Universe graph dedupes with its own page).
 *
 * Requirements for data to appear: a logged-in user (authStore.token), a
 * selected brand (workspaceStore.selectedBrandId), and the backend reachable
 * (localhost:5000/api locally). With no brand selected every query is disabled
 * and the views render their empty states.
 *
 * The agent chat rail is still canned (see ./mock) — its own live wiring
 * (useBrandUniverseChat etc.) is a separate follow-up.
 */
import { useMemo } from "react";
import type { UniverseGraph } from "@/types/brandUniverse.type";
import type { AgentRun } from "@/types/agent.type";
import type { MarketSignal, SignalKind } from "@/types/marketSignal.type";
import { EVERGREEN_CANDIDATE_KINDS } from "@/types/marketSignal.type";
import type { StrategyPillar, StrategyStatus } from "@/types/strategy.type";
import type {
  CalendarMonthPlan,
  CalendarPlanSlot,
  CalendarPlanStatus,
} from "@/types/contentCalendar.type";
import { isPlanApprovedLike } from "@/types/contentCalendar.type";
import type { FactProposal } from "@/types/chat.type";
import { useWorkspaceStore } from "@/stores/workspaceStore";
import { useAuthStore } from "@/stores/authStore";
import { useSelectedOrganization } from "@/hooks/useCurrentUser";
import { useGetBrands } from "@/hooks/useGetBrands";
import { useBrandUniverseGraph } from "@/app/routes/dashboard/api/useBrandUniverseGraph";
import { useAgentRunsByBrand } from "@/app/routes/dashboard/api/useAgentRuns";
import { useMarketSignals } from "@/app/routes/dashboard/market-signals/api/useMarketSignals";
import { validUntilFromWeeks } from "@/app/routes/dashboard/market-signals/components/signalMeta";
import {
  useStrategies,
  useStrategyDetail,
} from "@/app/routes/dashboard/strategy/api/useStrategies";
import {
  useCalendarPlans,
  useCalendarPlan,
  useCalendarPillars,
} from "@/app/routes/dashboard/content-calendar/api/useContentCalendar";
import { useBrandUniversePendingFactProposals } from "@/app/routes/dashboard/api/useBrandUniversePendingFactProposals";
import {
  useSocialConnectionStats,
  useSocialConnections,
  type SocialAccountStats,
  type SocialConnection,
} from "@/app/routes/dashboard/settings/api/useSocialConnections";
import {
  currentYearMonth,
  monthLabel,
  type YearMonth,
} from "@/app/routes/dashboard/content-calendar/calendar.utils";
import { buildUniverseLayout, type StageFact, type Ring } from "./universeLayout";
import type { ResolvedSubject } from "./universeTaxonomy";

/** Common async envelope every hook returns (mirrors React Query). */
type Async<T> = T & { isLoading: boolean; isError: boolean; refetch: () => void };

const EMPTY_GRAPH: UniverseGraph = { nodes: [], edges: [], entities: [] };

/** Active brand id for the cockpit — the workspace's selected brand. */
function useBrandId(): string | null {
  return useWorkspaceStore((s) => s.selectedBrandId);
}

/**
 * Brand universe graph + derived orbit layout (rings + facts) from the real
 * graph. New/established split is computed against the real clock.
 */
export function useUniverse(options: { enabled?: boolean } = {}): Async<{
  graph: UniverseGraph;
  facts: StageFact[];
  rings: Ring[];
  /** Full display taxonomy INCLUDING planned subjects with no concepts. `rings`
   *  holds only the ones that can carry facts, so the rail reads this to show
   *  Category and Culture as stated intent. */
  subjects: ResolvedSubject[];
  total: number;
  newCount: number;
  /** True while the graph is being (re)read, so a caller can tell "not in the
   *  graph" from "not in the graph yet". */
  isFetching?: boolean;
}> {
  const selectedBrandId = useBrandId();
  // `enabled: false` (the dev fixture harness) keeps the graph read off entirely.
  const brandId = options.enabled === false ? null : selectedBrandId;
  const { data, isLoading, isError, isFetching, refetch } = useBrandUniverseGraph(brandId);
  const graph = data ?? EMPTY_GRAPH;
  const { facts, rings, subjects } = useMemo(() => buildUniverseLayout(graph), [graph]);
  const total = facts.length;
  const newCount = facts.filter((f) => f.kind === "new").length;
  return { graph, facts, rings, subjects, total, newCount, isLoading, isError, isFetching, refetch: () => void refetch() };
}

/** Pending / active market signals (real shapes). */
export function useSignals(options: { enabled?: boolean } = {}): Async<{
  pendingSignals: MarketSignal[];
  activeSignals: MarketSignal[];
  signalsError: boolean;
}> {
  const selectedBrandId = useBrandId();
  // `enabled: false` (a guest's shell) keeps the signals query from firing at all.
  const brandId = options.enabled === false ? null : selectedBrandId;
  const signals = useMarketSignals(brandId);
  const pendingSignals = useMemo(
    () => signals.signals.filter((s) => s.status === "pending"),
    [signals.signals],
  );
  // Same list, filtered to active — drives the "N live" count on the Signals tile.
  const activeSignals = useMemo(
    () => signals.signals.filter((s) => s.status === "active"),
    [signals.signals],
  );
  return {
    pendingSignals,
    activeSignals,
    signalsError: signals.isError,
    isLoading: signals.isLoading,
    isError: signals.isError,
    refetch: () => {
      signals.invalidate();
    },
  };
}

function defaultValidUntil(signal: MarketSignal): string | null {
  if (signal.recommendedValidUntil) return signal.recommendedValidUntil;
  if (EVERGREEN_CANDIDATE_KINDS.includes(signal.kind as SignalKind)) return null;
  return validUntilFromWeeks(4);
}

/** Per-call success/error hooks so a view can confirm a decision only on a real
    server success and revert its optimistic state on failure. */
export type MutationCallbacks = { onSuccess?: () => void; onError?: () => void };

/** Accept/dismiss pending signals from the home cockpit (uses AI-suggested or default lifetime). */
export function usePendingSignalActions() {
  const brandId = useBrandId();
  const { acceptSignal, dismissSignal } = useMarketSignals(brandId);

  return {
    accept: (signal: MarketSignal, cbs?: MutationCallbacks) =>
      acceptSignal.mutate({ signalId: signal.id, validUntil: defaultValidUntil(signal) }, cbs),
    dismiss: (signalId: string, cbs?: MutationCallbacks) => dismissSignal.mutate(signalId, cbs),
    isPending: acceptSignal.isPending || dismissSignal.isPending,
  };
}

export function signalEvidenceStats(signal: MarketSignal) {
  const obs = signal.groundingObservations;
  const hosts = obs.map((o) => {
    if (!o.sourceUrl) return o.id;
    try {
      return new URL(o.sourceUrl).host.replace(/^www\./, "");
    } catch {
      return o.sourceUrl;
    }
  });
  return { supportCount: obs.length, distinctSourceCount: new Set(hosts).size };
}

/** Title-case an agent-type slug into a label ("trend_intelligence" → "Trend Intelligence"). */
export function humanizeAgentType(type: string): string {
  return type
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
}

/** Runs finished within this window count as "ran recently" (heartbeat + summary line). */
const AGENT_RECENT_MS = 24 * 60 * 60 * 1000;
/** Exported so a surface drawing a run's status reads the SAME set `allHealthy`
 *  is derived from, rather than keeping a second list that can drift from it. */
export const AGENT_FAILED_STATUSES = new Set(["failed", "timed_out", "error"]);

export type AgentLiveness = {
  runs: AgentRun[];
  /** currently executing runs */
  running: AgentRun[];
  /** scheduled-but-not-started runs */
  queued: AgentRun[];
  /** runs finished within AGENT_RECENT_MS, newest first */
  finishedRecent: AgentRun[];
  /** the most recent finishedAt across all runs (null if none has ever finished) */
  lastFinishedAt: string | null;
  /** no recent run failed / errored */
  allHealthy: boolean;
};

/**
 * Derive the liveness read from a run list. `now` defaults to the real clock (as
 * buildUniverseLayout does) so the impure read stays out of the render path.
 */
function deriveLiveness(runs: AgentRun[], now: number = Date.now()): Omit<AgentLiveness, "runs"> {
  const running = runs.filter((r) => r.status === "running");
  const queued = runs.filter((r) => r.status === "queued");
  const finishedSorted = runs
    .filter((r) => r.finishedAt != null && now - Date.parse(r.finishedAt) <= AGENT_RECENT_MS)
    .sort((a, b) => (a.finishedAt! < b.finishedAt! ? 1 : -1));
  // Distinct agents, newest-run-per-type: a double-finish must not read as two agents.
  const seenType = new Set<string>();
  const finishedRecent = finishedSorted.filter((r) => {
    if (seenType.has(r.agentType)) return false;
    seenType.add(r.agentType);
    return true;
  });
  const lastFinishedAt = runs.reduce<string | null>(
    (acc, r) => (r.finishedAt && (!acc || r.finishedAt > acc) ? r.finishedAt : acc),
    null,
  );
  const allHealthy = !finishedRecent.some(
    (r) => AGENT_FAILED_STATUSES.has(r.status) || r.error != null,
  );
  return { running, queued, finishedRecent, lastFinishedAt, allHealthy };
}

/**
 * Live agent runs for the selected brand (polls while any run is active, per
 * useAgentRunsByBrand). Derives agent liveness for the activity column and summary line.
 */
export function useAgentRuns(): Async<AgentLiveness> {
  const brandId = useBrandId();
  const { data, isLoading, isError, refetch } = useAgentRunsByBrand(brandId);
  const runs = useMemo(() => data ?? [], [data]);
  const derived = useMemo(() => deriveLiveness(runs), [runs]);
  return { runs, ...derived, isLoading, isError, refetch: () => void refetch() };
}

/** Strategy meta as the views need it (subset of Strategy + parent fields). */
export type StrategyMeta = { title: string; status: StrategyStatus };

const EMPTY_META: StrategyMeta = { title: "", status: "draft" };

/**
 * Active social strategy + pillars. Picks the active (else most recent) strategy
 * from the brand's list, then loads its detail.
 *
 * CURRENTLY UNREAD: no surface consumes this hook today (the home dropped its
 * strategy tile), so nothing on screen is wired to it.
 */
export function useStrategy(): Async<{ meta: StrategyMeta; pillars: StrategyPillar[] }> {
  const brandId = useBrandId();
  const { strategies, isLoading: listLoading, isError: listError } = useStrategies(brandId);
  const active =
    strategies.find((s) => s.status === "active") ?? strategies[0] ?? null;
  const { detail, isLoading: detailLoading, isError: detailError, refetch } = useStrategyDetail(brandId, active?.id ?? null);

  const meta: StrategyMeta = detail
    ? {
        title: detail.strategy.title,
        status: detail.strategy.status,
      }
    : EMPTY_META;

  return {
    meta,
    pillars: detail?.pillars ?? [],
    isLoading: listLoading || (!!active && detailLoading),
    // Real query error (list or detail) so a failed fetch shows the tile's error
    // state rather than masquerading as an honest "no strategy yet" empty.
    isError: listError || detailError,
    refetch,
  };
}

/**
 * The four states a month is ever SHOWN in. The wire carries thirteen, but nine
 * of them (PENDING_APPROVAL, and IN_CLIENT_REVIEW … MONTH_SIGNED_OFF) are LEGACY
 * values that behave exactly like APPROVED everywhere in the app, so they are
 * folded rather than rendered as states of their own.
 */
export type MonthLiveStatus = "DRAFT" | "GENERATING" | "APPROVED" | "ARCHIVED";

/**
 * The fold. `isPlanApprovedLike` is the same predicate every other gate in the
 * app uses for it, and the switch beneath is exhaustive on purpose: a value
 * added to the wire fails the build here instead of quietly reading as some
 * other month's state.
 */
function monthLiveStatus(status: CalendarPlanStatus): MonthLiveStatus {
  if (isPlanApprovedLike(status)) return "APPROVED";
  switch (status) {
    case "DRAFT":
    case "GENERATING":
    case "ARCHIVED":
      return status;
    // Claimed by isPlanApprovedLike above; named so this switch stays exhaustive.
    case "APPROVED":
    case "PENDING_APPROVAL":
    case "IN_CLIENT_REVIEW":
    case "CHANGES_REQUESTED":
    case "CLIENT_APPROVED":
    case "BRIEFS_GENERATING":
    case "BRIEFS_DRAFT":
    case "BRIEFS_IN_REVIEW":
    case "BRIEFS_CHANGES_REQUESTED":
    case "MONTH_SIGNED_OFF":
      return "APPROVED";
  }
}

/**
 * One month in the pipeline: which month it is, where it is stuck, and how big
 * it was meant to be.
 *
 * Everything here comes off the PLANS LIST, which is one call the home already
 * makes, so a row costs nothing. That is also the ceiling on what a row may
 * say: `useCalendar` detail-loads exactly ONE plan, so only the month flagged
 * `isCurrent` has slots in hand and only that row may carry a fill count, a
 * published count, or any per-day state. For every other month the list row is
 * the whole truth, and inventing more would take one call each.
 */
export type MonthPipelineRow = {
  id: string;
  year: number;
  month: number;
  /** "August 2026". */
  label: string;
  status: MonthLiveStatus;
  /** How many posts this month was planned for (pro-rated for a partial month). */
  targetSlotCount: number;
  /** Non-null when plan generation failed on this month. */
  generationError: string | null;
  /** The one month whose slots were actually fetched. */
  isCurrent: boolean;
};

/**
 * The plans list as pipeline rows, newest first. Exported so the dev harness
 * runs the SAME derivation the app does rather than a hand-written copy of it
 * that can drift (the legacy fold is exactly the kind of rule that drifts).
 */
export function buildMonthRows(
  plans: CalendarMonthPlan[],
  now: YearMonth,
): MonthPipelineRow[] {
  return [...plans]
    .sort((a, b) => b.year - a.year || b.month - a.month)
    .map((p) => ({
      id: p.id,
      year: p.year,
      month: p.month,
      label: monthLabel(p),
      status: monthLiveStatus(p.status),
      targetSlotCount: p.targetSlotCount,
      generationError: p.generationError,
      isCurrent: p.year === now.year && p.month === now.month,
    }));
}

/**
 * Current month's calendar slots (real shapes), plus the decision reads the home
 * needs. Both queries were already fired here and most of their payload thrown
 * away at the return. Everything below is derived from what is already in hand,
 * so this adds ZERO network calls.
 */
export function useCalendar(): Async<{
  month: { year: number; month: number; label: string };
  slots: CalendarPlanSlot[];
  planId: string | null;
  /** The current month's plan status, or null when the month has no plan row. */
  planStatus: CalendarPlanStatus | null;
  /**
   * Every month the brand has, newest first, as pipeline rows. Derived from the
   * plans list the home already reads, so it costs nothing extra, and it carries
   * nothing that list did not answer (see MonthPipelineRow).
   */
  months: MonthPipelineRow[];
  /**
   * Current-month posts the client is deciding on right now. PIPELINE only: a
   * DIRECT quick post is born SIGNED_OFF and never sees a client review, so
   * counting one here would invent a review that cannot exist.
   */
  awaitingClient: number;
  /** planId !== null. Tells "no month planned yet" apart from "empty month".
      CURRENTLY UNREAD: no surface consumes this field today. */
  hasPlan: boolean;
  /**
   * The MONTHS BAND's own phase, and the reason it is separate: `months` is
   * derived 100% from the plans LIST, while `isLoading` / `isError` below also
   * carry the CURRENT MONTH'S DETAIL read. Gating the band on the combined flags
   * held it on skeletons (and could hand it an error) while its own data was
   * already in hand and only the detail was still in flight.
   *
   * Anything slot-derived (the fill count, the published count, awaitingClient)
   * genuinely needs the detail and keeps the combined flags.
   */
  monthsLoading: boolean;
  monthsError: boolean;
}> {
  const brandId = useBrandId();
  const ym = useMemo(() => currentYearMonth(), []);
  const plans = useCalendarPlans(brandId);
  const current =
    plans.data?.find((p) => p.year === ym.year && p.month === ym.month) ?? null;
  const planId = current?.id ?? null;
  const plan = useCalendarPlan(planId);

  const slots = useMemo(() => plan.data?.slots ?? [], [plan.data]);

  const awaitingClient = useMemo(
    () => slots.filter((s) => s.status === "IN_CLIENT_REVIEW" && s.origin === "PIPELINE").length,
    [slots],
  );

  const months = useMemo(() => buildMonthRows(plans.data ?? [], ym), [plans.data, ym]);

  return {
    month: { year: ym.year, month: ym.month, label: monthLabel(ym) },
    slots,
    planId,
    // The month's own read wins — it is the one that polls, so the list is a
    // round trip behind — with the list row answering while the detail is still
    // in flight.
    planStatus: plan.data?.status ?? current?.status ?? null,
    months,
    awaitingClient,
    hasPlan: planId !== null,
    // The band reads the list and nothing else, so it answers when the list does.
    monthsLoading: plans.isLoading,
    monthsError: plans.isError,
    isLoading: plans.isLoading || (!!planId && plan.isLoading),
    isError: plans.isError || plan.isError,
    refetch: () => {
      void plans.refetch();
      void plan.refetch();
    },
  };
}

/**
 * Is this brand's calendar planned against a strategy? The pillars read answers
 * it in one call.
 *
 * A null `strategyId` no longer BLOCKS anything: the planner lays out a month
 * for a brand with no strategy too, from its DNA, its industry and the live
 * signals, and those slots simply carry no pillar. So copy built on this is a
 * nudge towards a better month, never a statement that planning is refused.
 *
 * This is a fact about the BRAND, not about a month — there is no "blocked"
 * status anywhere in the calendar schema. A failed call renders the error
 * state, never "plannable".
 *
 * `reason` is the only field that may drive copy: it is non-null ONLY when the
 * call answered and the answer was a null strategyId. Until then (no brand
 * selected, in flight, failed) `plannable` is false with a null reason, which
 * means "not answered yet", not "has no strategy".
 */
export function useBrandPlannable(): Async<{
  plannable: boolean;
  reason: "no-strategy" | null;
}> {
  const brandId = useBrandId();
  const { data, isLoading, isError, refetch } = useCalendarPillars(brandId);
  const plannable = data ? data.strategyId !== null : false;
  return {
    plannable,
    reason: data && data.strategyId === null ? "no-strategy" : null,
    isLoading,
    isError,
    refetch: () => void refetch(),
  };
}

/**
 * Facts waiting to join the universe. A genuinely different queue from the
 * pending market signals: accepting a signal only sets it ACTIVE, while
 * accepting one of these writes a BrandUniverseFact.
 *
 * Shares the Universe page's cache key, so opening either surface warms the
 * other.
 */
export function useFactProposals(): Async<{ proposals: FactProposal[] }> {
  const brandId = useBrandId();
  const { pending, isLoading, isError, invalidatePending } =
    useBrandUniversePendingFactProposals(brandId);
  return {
    proposals: pending,
    isLoading,
    isError,
    refetch: invalidatePending,
  };
}

/**
 * Point-in-time reach and engagement per connected account. Every number is
 * nullable and there is no history table behind them (both metric writes are
 * upserts), so consumers may show the value and its `lastSyncedAt` and nothing
 * else: no delta, no trend, no growth.
 */
export function useSocialStats(): Async<{ stats: SocialAccountStats[] }> {
  const brandId = useBrandId();
  const { data, isLoading, isError, refetch } = useSocialConnectionStats(brandId);
  /* Memoized like useCalendar's `slots`: a fresh `[]` every render makes every
     downstream useMemo keyed on it recompute on every render, and the home
     joins this list against the connections list on each pass. */
  const stats = useMemo(() => data ?? [], [data]);
  return {
    stats,
    isLoading,
    isError,
    refetch: () => void refetch(),
  };
}

/**
 * The connected accounts themselves, which is a SECOND read and not optional.
 *
 * `SocialAccountStats` carries no handle and no connection status, so an
 * account's handle (`accounts[].username`) and its `NEEDS_REAUTH` state exist
 * only here. Anything rendering "platform, handle" or explaining a stale card
 * has to read both queries or invent the fields. `accountId` on a stats row is
 * the join key back to `accounts[].id`.
 *
 * +1 call. It already polls at POLL_CURATION_MS and shares the Settings page's
 * cache key, so opening either surface warms the other.
 */
export function useConnectedAccounts(): Async<{ connections: SocialConnection[] }> {
  const brandId = useBrandId();
  const { data, isLoading, isError, refetch } = useSocialConnections(brandId);
  /* Same reason as useSocialStats above: the identity of this array is a
     dependency of the home's landing-row join. */
  const connections = useMemo(() => data ?? [], [data]);
  return {
    connections,
    isLoading,
    isError,
    refetch: () => void refetch(),
  };
}

/** Initials from a full name (e.g. "Mara Lind" → "ML"). */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Brand + viewer identity from workspace and auth stores.
 */
export function useBrandIdentity(options: { enabled?: boolean } = {}) {
  const brandId = useBrandId();
  const user = useAuthStore((s) => s.user);
  const { selectedOrganization } = useSelectedOrganization();
  const { data: brands } = useGetBrands(options);

  const brand = brands?.find((b) => b.id === brandId) ?? null;
  const name = brand?.name ?? "Your brand";
  const fullName = user?.name ?? "";
  const userFirst = fullName.split(/\s+/)[0] || "there";
  const userInitials = initialsOf(fullName);
  const role = selectedOrganization?.role ?? "";

  const { dateLabel, weekday } = useMemo(() => {
    const now = new Date();
    return {
      dateLabel: now
        .toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
        .toUpperCase(),
      weekday: now.toLocaleDateString("en-US", { weekday: "long" }),
    };
  }, []);

  return {
    id: brandId ?? "",
    name,
    /* Presigned by the brand list endpoint; null when the brand has no logo,
       so every consumer keeps its monogram fallback. */
    logoUrl: brand?.logoUrl ?? null,
    initial: (name.charAt(0) || "·").toUpperCase(),
    curatorName: fullName,
    curatorRole: role || "Curator",
    curatorInitials: userInitials,
    userFirst,
    userName: fullName,
    userRole: role,
    userInitials,
    dateLabel,
    weeklyReadBy: fullName ? `${userFirst}'s weekly read` : "Weekly read",
    weekday,
  };
}
